import React from 'react'
const { useState, useEffect, useCallback, useMemo, useRef } = React;

import {
  AirtableAPI, TABLE_IDS, CURRENT_USER, COMPANY_PROFILE,
  callOpenAI, navSetUrl, SOURCE_OPTIONS, OPP_STAGES, WON_STAGES, CLOSED_STAGES,
  BENCH_REPLY_HIGH, BENCH_REPLY_LOW, BENCH_MEETING_HIGH, BENCH_MEETING_LOW,
  CHANNEL_BENCHMARKS, MESSAGE_PROMPTS, MESSAGE_PROMPT_DEFAULTS, saveMessagePrompts,
  resolvePromptTemplate, saveCompanyProfile, channelIcon, logoutUser,
  CLIENT_CONFIG, AUTH_TOKEN, loadBranding, BRANDING_LS_KEY,
} from '../globals.js';
import {
  F, linkedIds, resolveLinked, InfoTip, REPLY_STATUSES, computeEnrichment,
  DIAGNOSIS_CONFIG, findDuplicateStakeholder, confirmDuplicateStakeholder,
  deriveStakeholderStatus, updateStakeholderStatus, STAKEHOLDER_STATUS_PRIORITY,
  STAKEHOLDER_STATUS_PROTECTED, activateAccountIfNeeded,
  formatCurrency, formatDate, strSimilarity, FileNotesRenderer,
} from '../utils.jsx';


function ICPSection({ data, goToSolution, api, onLogActivity, onAddRecord }) {
  const { icp = [], solutions = [] } = data;
  const isAdmin = CURRENT_USER?.role === 'admin';
  const [selected, setSelected] = React.useState(null);
  const [showIcpModal, setShowIcpModal] = React.useState(false);
  const [editingIcp, setEditingIcp] = React.useState(null);
  const [icpForm, setIcpForm] = React.useState({ name: '', industry: '', country: '', b2b: 'B2B', size: '', deal: '', pains: '', triggers: '', priorities: '', exclusions: '', solutionIds: [] });
  const [savingIcp, setSavingIcp] = React.useState(false);

  const ICP_B2B_OPTIONS = ['B2B', 'B2C', 'B2B/B2C'];

  const openNewIcp = () => {
    setEditingIcp(null);
    setIcpForm({ name: '', industry: '', country: '', b2b: 'B2B', size: '', deal: '', pains: '', triggers: '', priorities: '', exclusions: '', solutionIds: [] });
    setShowIcpModal(true);
  };

  const openEditIcp = (profile) => {
    setEditingIcp(profile);
    setIcpForm({
      name: F(profile, 'Name') || '',
      industry: F(profile, 'Industry') || '',
      country: F(profile, 'Country') || '',
      b2b: F(profile, 'B2B / B2C') || 'B2B',
      size: F(profile, 'Company Size') || '',
      deal: F(profile, 'Average deal size') || '',
      pains: F(profile, 'Pains') || '',
      triggers: (() => { const t = F(profile, 'Triggers (why now)'); return Array.isArray(t) ? t.join(', ') : (t || ''); })(),
      priorities: F(profile, 'Priorities') || '',
      exclusions: F(profile, 'Exclusions (Who is not a fit)') || '',
      solutionIds: linkedIds(profile, 'Solutions'),
    });
    setShowIcpModal(true);
  };

  const saveIcp = async () => {
    if (!icpForm.name.trim()) return;
    setSavingIcp(true);
    try {
      const a = api || new AirtableAPI();
      const fields = {
        'Name': icpForm.name.trim(),
        'Industry': icpForm.industry.trim(),
        'Country': icpForm.country.trim(),
        'B2B / B2C': icpForm.b2b,
        'Company Size': icpForm.size.trim(),
        'Average deal size': icpForm.deal.trim(),
        'Pains': icpForm.pains.trim(),
        'Priorities': icpForm.priorities.trim(),
        'Exclusions (Who is not a fit)': icpForm.exclusions.trim(),
      };
      if (icpForm.triggers.trim()) {
        fields['Triggers (why now)'] = icpForm.triggers.split(',').map(t => t.trim()).filter(Boolean);
      }
      if (icpForm.solutionIds && icpForm.solutionIds.length > 0) {
        fields['Solutions'] = icpForm.solutionIds.map(id => ({ id }));
      }
      if (editingIcp) {
        await a.updateRecord(TABLE_IDS.icp, editingIcp.id, fields);
      } else {
        const rec = await a.createRecord(TABLE_IDS.icp, fields);
        if (onAddRecord) onAddRecord('icp', { ...fields, id: rec.id });
      }
      setShowIcpModal(false);
      if (onLogActivity) onLogActivity();
    } catch (e) { console.error(e); window.__oikeToast('No se pudo guardar el perfil: ' + e.message, 'error'); }
    setSavingIcp(false);
  };

  const tagStyle = (bg, color) => ({ display:'inline-block', padding:'2px 10px', borderRadius:12, fontSize:11, fontWeight:600, background:bg, color:color, marginRight:4, marginBottom:4 });
  const fieldBlock = (label, value) => value ? (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:10, fontWeight:700, color:'var(--globant-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:13, color:'var(--globant-text)', lineHeight:1.6, whiteSpace:'pre-wrap' }}>{value}</div>
    </div>
  ) : null;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>Perfiles de cliente ideal</h1>
          <p style={{ color:'var(--globant-muted)', marginTop:4 }}>{icp.length} perfiles definidos</p>
        </div>
        {isAdmin && <button className="action-btn btn-primary" style={{ fontSize:12, padding:'8px 16px', marginTop:4 }} onClick={openNewIcp}>Nuevo perfil</button>}
      </div>

      {/* ICP Create/Edit Modal */}
      {showIcpModal && isAdmin && (() => {
        const iStyle = { width:'100%', padding:'8px 10px', background:'var(--globant-input)', border:'1px solid var(--globant-border)', borderRadius:6, color:'var(--globant-text)', fontSize:13, boxSizing:'border-box' };
        const lStyle = { fontSize:11, color:'var(--globant-muted)', fontWeight:600, marginBottom:4, textTransform:'uppercase', display:'block' };
        return (
          <div className="modal-overlay" onClick={() => setShowIcpModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:560, maxHeight:'88vh', overflowY:'auto' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
                <h3 style={{ margin:0 }}>{editingIcp ? 'Editar perfil' : 'Nuevo perfil'}</h3>
                <button onClick={() => setShowIcpModal(false)} style={{ background:'none', border:'none', color:'var(--globant-muted)', cursor:'pointer', fontSize:18 }}>✕</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                <div><label style={lStyle}>Nombre *<InfoTip text="Un nombre que describa a este cliente, por ejemplo 'Gerente comercial' o 'Fundadora que escala'." /></label><input style={iStyle} value={icpForm.name} onChange={e => setIcpForm(p => ({ ...p, name: e.target.value }))} autoFocus placeholder="Ej.: Gerente comercial" /></div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <div><label style={lStyle}>Industria<InfoTip text="Industria o sector de este perfil. Podés poner varias separadas por /." /></label><input style={iStyle} value={icpForm.industry} onChange={e => setIcpForm(p => ({ ...p, industry: e.target.value }))} placeholder="Ej.: Software / Consultoría" /></div>
                  <div><label style={lStyle}>País o región<InfoTip text="Dónde están estos clientes: un país, una región o varios mercados." /></label><input style={iStyle} value={icpForm.country} onChange={e => setIcpForm(p => ({ ...p, country: e.target.value }))} placeholder="Ej.: Chile / LATAM" /></div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                  <div><label style={lStyle}>B2B / B2C<InfoTip text="¿Este cliente vende a empresas (B2B), a personas (B2C) o a ambos?" /></label><select style={iStyle} value={icpForm.b2b} onChange={e => setIcpForm(p => ({ ...p, b2b: e.target.value }))}>{ICP_B2B_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select></div>
                  <div><label style={lStyle}>Tamaño de la empresa<InfoTip text="Cantidad de personas. Podés usar rangos como '5–50' o '50+'." /></label><input style={iStyle} value={icpForm.size} onChange={e => setIcpForm(p => ({ ...p, size: e.target.value }))} placeholder="e.g. 5–50" /></div>
                  <div><label style={lStyle}>Monto típico<InfoTip text="Valor habitual de un contrato con este cliente. Ayuda a calibrar el esfuerzo comercial." /></label><input style={iStyle} value={icpForm.deal} onChange={e => setIcpForm(p => ({ ...p, deal: e.target.value }))} placeholder="e.g. $10K–$50K" /></div>
                </div>
                <div><label style={lStyle}>Dolores<InfoTip text="Los problemas principales de este perfil que tu servicio resuelve. Sé específica." /></label><textarea style={{ ...iStyle, minHeight:70, resize:'vertical' }} value={icpForm.pains} onChange={e => setIcpForm(p => ({ ...p, pains: e.target.value }))} placeholder="¿Qué problemas tiene este perfil?" /></div>
                <div><label style={lStyle}>Disparadores (separados por coma)<InfoTip text="Situaciones que generan urgencia para actuar ahora. Ej.: metas no cumplidas, equipo que crece, nueva dirección." /></label><input style={iStyle} value={icpForm.triggers} onChange={e => setIcpForm(p => ({ ...p, triggers: e.target.value }))} placeholder="Ej.: meta no cumplida, nueva contratación, equipo que crece" /></div>
                <div><label style={lStyle}>Prioridades<InfoTip text="Lo que más le importa a este cliente. Sirve para alinear tus mensajes." /></label><input style={iStyle} value={icpForm.priorities} onChange={e => setIcpForm(p => ({ ...p, priorities: e.target.value }))} placeholder="Ej.: constancia, previsibilidad" /></div>
                <div><label style={lStyle}>Exclusiones (a quién no le sirve)<InfoTip text="Rasgos que descartan a un prospecto para este perfil. Ayudan a calificar más rápido." /></label><input style={iStyle} value={icpForm.exclusions} onChange={e => setIcpForm(p => ({ ...p, exclusions: e.target.value }))} placeholder="Ej.: sin equipo, sin ventas todavía" /></div>
                <div>
                  <label style={lStyle}>Servicios asociados<InfoTip text="¿Qué servicios le podés ofrecer a este perfil? Elegí todos los que apliquen." /></label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                    {solutions.map(s => {
                      const sid = s.id;
                      const selected = (icpForm.solutionIds || []).includes(sid);
                      return (
                        <span key={sid}
                          onClick={() => setIcpForm(p => ({
                            ...p,
                            solutionIds: selected
                              ? p.solutionIds.filter(id => id !== sid)
                              : [...(p.solutionIds || []), sid]
                          }))}
                          style={{ cursor: 'pointer', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                            background: selected ? 'rgba(91,191,181,0.2)' : 'rgba(30,42,44,0.05)',
                            color: selected ? 'var(--globant-green)' : 'var(--globant-muted)',
                            border: `1px solid ${selected ? 'rgba(91,191,181,0.4)' : 'rgba(30,42,44,0.1)'}`,
                            transition: 'all 0.15s'
                          }}>
                          {selected ? '✓ ' : ''}{F(s, 'Name')}
                        </span>
                      );
                    })}
                    {solutions.length === 0 && <span style={{ fontSize: 12, color: 'var(--globant-muted)' }}>No hay servicios</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
                  <button className="action-btn btn-ghost" onClick={() => setShowIcpModal(false)}>Cancelar</button>
                  <button className="action-btn btn-primary" onClick={saveIcp} disabled={savingIcp || !icpForm.name.trim()}>
                    {savingIcp ? 'Guardando…' : editingIcp ? 'Guardar cambios' : 'Crear perfil'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {icp.length === 0 && (
        <div className="card" style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🎯</div>
          <div style={{ color:'var(--globant-muted)', fontSize:14 }}>Todavía no hay perfiles. Creá el primero para empezar.</div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(320px, 1fr))', gap:16 }}>
        {icp.map(profile => {
          const name = F(profile, 'Name') || 'Unnamed';
          const industry = F(profile, 'Industry') || '';
          const size = F(profile, 'Company Size') || '';
          const country = F(profile, 'Country') || '';
          const b2b = F(profile, 'B2B / B2C') || '';
          const deal = F(profile, 'Average deal size') || '';
          const pains = F(profile, 'Pains') || '';
          const triggers = F(profile, 'Triggers (why now)') || '';
          const priorities = F(profile, 'Priorities') || '';
          const exclusions = F(profile, 'Exclusions (Who is not a fit)') || '';
          const solNames = F(profile, 'Name (from Solutions)') || '';
          const solNamesArr = Array.isArray(solNames) ? solNames : (solNames ? [String(solNames)] : []);
          const linkedSolIds = linkedIds(profile, 'Solutions');
          const linkedSols = linkedSolIds.map(id => solutions.find(s => s.id === id)).filter(Boolean);
          const isSelected = selected === profile.id;

          return (
            <div key={profile.id} className="card"
              style={{ cursor:'pointer', border: isSelected ? '1.5px solid var(--globant-green)' : '1px solid var(--globant-border)', transition:'border-color 0.15s' }}
              onClick={() => setSelected(isSelected ? null : profile.id)}>

              {/* Header */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:16, fontWeight:800, color:'var(--globant-text)', marginBottom:4 }}>{name}</div>
                  <div style={{ fontSize:12, color:'var(--globant-muted)' }}>{industry}</div>
                </div>
                <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                  {b2b && <span style={tagStyle('rgba(91,191,181,0.15)', 'var(--globant-green)')}>{b2b}</span>}
                  {isAdmin && <button onClick={e => { e.stopPropagation(); openEditIcp(profile); }} style={{ background:'rgba(30,42,44,0.05)', border:'1px solid var(--globant-border)', borderRadius:6, padding:'3px 8px', fontSize:11, color:'var(--globant-muted)', cursor:'pointer' }}>✏️</button>}
                </div>
              </div>

              {/* Quick tags */}
              <div style={{ marginBottom:12 }}>
                {size && <span style={tagStyle('rgba(96,165,250,0.12)', '#2D5F99')}>{size}</span>}
                {country && <span style={tagStyle('rgba(251,191,36,0.12)', '#9A6212')}>{country}</span>}
                {deal && <span style={tagStyle('rgba(74,222,128,0.12)', '#1E7B4F')}>💰 {deal}</span>}
              </div>

              {/* Pains preview */}
              {pains && (
                <div style={{ fontSize:12, color:'var(--globant-muted)', lineHeight:1.5, marginBottom:8 }}>
                  <span style={{ color:'#B4362C', fontWeight:700 }}>Dolor: </span>
                  {pains.length > 100 ? pains.slice(0,100)+'...' : pains}
                </div>
              )}

              {/* Solutions linked */}
              {(linkedSols.length > 0 || solNamesArr.length > 0) && (
                <div style={{ fontSize:11, color:'var(--globant-muted)', marginBottom:8 }}>
                  <span style={{ fontWeight:600 }}>Servicios: </span>
                  {linkedSols.length > 0
                    ? linkedSols.map((sol, i) => (
                        <span key={sol.id}>
                          {goToSolution
                            ? <span style={{ color:'var(--globant-green)', cursor:'pointer', fontWeight:600 }}
                                onClick={e => { e.stopPropagation(); goToSolution(sol.id); }}>
                                {F(sol, 'Name')}
                              </span>
                            : <span>{F(sol, 'Name')}</span>
                          }
                          {i < linkedSols.length - 1 ? ', ' : ''}
                        </span>
                      ))
                    : solNamesArr.join(', ')
                  }
                </div>
              )}

              {/* Expand */}
              <div style={{ fontSize:11, color:'var(--globant-green)', fontWeight:600, marginTop:4 }}>
                {isSelected ? 'Ver menos' : 'Ver perfil completo'}
              </div>

              {/* Expanded detail */}
              {isSelected && (
                <div style={{ marginTop:16, paddingTop:16, borderTop:'1px solid var(--globant-border)' }}>
                  {fieldBlock('Disparadores: por qué ahora', triggers)}
                  {fieldBlock('Prioridades', priorities)}
                  {fieldBlock('Dolores', pains)}
                  {exclusions && (
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'#B4362C', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:4 }}>Exclusiones: a quién no le sirve</div>
                      <div style={{ fontSize:13, color:'#B4362C', lineHeight:1.6 }}>{exclusions}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ SOLUTIONS HUB ============

export default ICPSection;
